import React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableFooter from "@mui/material/TableFooter";
import ExpandMoreSharpIcon from "@mui/icons-material/ExpandMoreSharp";
import RememberMeIcon from "@mui/icons-material/RememberMe";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import _ from "lodash";

import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  ColumnDef,
  flexRender,
} from "@tanstack/react-table";

type PlayerGame = {
  _id: string;
  playerID: {
    _id: string;
    firstname: string;
    lastname: string;
  };
  gameID: { _id: string };
  teamID: { _id: string; name: string };
  pts: number;
  fgm: number;
  threes: number;
  twos: number;
  freeThrows: number;
  freeThrowsAttempts: number;
  fouls: number;
  techs: number;
  unsportmanlike: number;
  bestDefender: number;
  win: number;
  ghost: boolean;
};

const GamesList = ({ onData, prop, playerGames }) => {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [loading, setLoading] = React.useState(true);
  const [data, setData] = React.useState<PlayerGame[]>([]);

  // Simulate processing or delayed loading
  React.useEffect(() => {
    if (!playerGames) return;
    setLoading(true);
    const timer = setTimeout(() => {
      setData(playerGames);
      setLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [playerGames]);

  const columns = React.useMemo<ColumnDef<PlayerGame>[]>(
    () => [
      {
        header: "Info",
        columns: [
          {
            header: "Full Name",
            accessorFn: (row) =>
              `${row.playerID.firstname} ${row.playerID.lastname}`,
            cell: (info) => (
              <Button
                variant="contained"
                color="inherit"
                sx={{
                  bgcolor: "black",
                  width: "100%",
                  color: "white",
                  fontWeight: "600",
                  ":hover": { bgcolor: "#bf0202" },
                }}
                href={`/players/${info.row.original.playerID._id}`}
              >
                {info.getValue()}
              </Button>
            ),
            footer: () => <div>Total</div>,
            sortDescFirst: true,
          },
          {
            id: "_id",
            header: "⭐",
            accessorFn: (row) => `${row.playerID._id}`,
            cell: (info) => (
              <button
                onClick={() => {
                  onData(true);
                  prop(info.getValue());
                }}
              >
                <RememberMeIcon />
              </button>
            ),
            footer: () => null,
            enableSorting: false,
            enableColumnFilter: false,
          },
        ],
      },
      {
        header: "Stats",
        columns: [
          {
            header: "PTS",
            accessorKey: "pts",
            cell: (info) => info.getValue(),
            footer: () => <span>{_.sumBy(data, "pts")}</span>,
          },
          {
            header: "FGM",
            accessorKey: "fgm",
            cell: (info) => info.getValue(),
            footer: () => <span>{_.sumBy(data, "fgm")}</span>,
          },
          {
            header: "3PM",
            accessorKey: "threes",
            cell: (info) => info.getValue(),
            footer: () => <span>{_.sumBy(data, "threes")}</span>,
          },
          {
            header: "FTM",
            accessorKey: "freeThrows",
            cell: (info) => info.getValue(),
            footer: () => <span>{_.sumBy(data, "freeThrows")}</span>,
          },
          {
            header: "FTA",
            accessorKey: "freeThrowsAttempts",
            cell: (info) => info.getValue(),
            // ✅ Total (not avg)
            footer: () => (
              <span>
                {_.sumBy(data.filter((d) => d.freeThrowsAttempts > 0), "freeThrowsAttempts")}
              </span>
            ),
          },
          {
            header: "FT%",
            accessorFn: (row) =>
              row.freeThrowsAttempts > 0
                ? (row.freeThrows / row.freeThrowsAttempts) * 100
                : 0,
            cell: (info) => (
              <span>{Number(info.getValue()).toFixed(1)}%</span>
            ),
            sortDescFirst: true,
            // ✅ Average only if FTA > 0
            footer: () => {
              const valid = data.filter((d) => d.freeThrowsAttempts > 0);
              const avg =
                valid.length > 0
                  ? _.mean(
                      valid.map(
                        (d) =>
                          (d.freeThrows / d.freeThrowsAttempts) * 100
                      )
                    )
                  : 0;
              return <span>{avg.toFixed(1)}%</span>;
            },
          },
          {
            header: "Fouls",
            accessorKey: "fouls",
            cell: (info) => info.getValue(),
            footer: () => <span>{_.sumBy(data, "fouls")}</span>,
          },
        ],
      },
    ],
    [data]
  );

  function LocalTable({
    data,
    columns,
  }: {
    data: PlayerGame[];
    columns: ColumnDef<PlayerGame>[];
  }) {
    const table = useReactTable({
      data,
      columns,
      state: { sorting },
      onSortingChange: setSorting,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
    });

    return (
      <Box sx={{ width: "100%" }}>
        <TableContainer component={Paper}>
          <Table>
            {/* Header */}
            <TableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableCell
                      key={header.id}
                      colSpan={header.colSpan}
                      sx={{
                        fontFamily: "Poppins",
                        fontWeight: "bold",
                        textAlign: "center",
                      }}
                    >
                      {header.isPlaceholder ? null : (
                        <div
                          {...{
                            className: header.column.getCanSort()
                              ? "cursor-pointer select-none flex-col flex align-center items-center sortLabel"
                              : "inline-block flex justify-center",
                            onClick: header.column.getToggleSortingHandler(),
                          }}
                        >
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {header.column.getCanSort() && (
                            <ExpandMoreSharpIcon
                              className={`inline-block sort ${
                                header.column.getIsSorted() === "asc"
                                  ? "sortFlip"
                                  : ""
                              }`}
                            />
                          )}
                        </div>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>

            {/* Body */}
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      sx={{
                        textAlign: "center",
                        fontFamily: "Poppins",
                        padding: "4px",
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>

            {/* ✅ Footer (Red Background) */}
            <TableFooter sx={{ bgcolor: "#bf0202" }}>
              {table.getFooterGroups().map((footerGroup) => (
                <TableRow key={footerGroup.id}>
                  {footerGroup.headers.map((header) =>
                    header.column.columnDef.footer ? (
                      <TableCell
                        key={header.id}
                        sx={{
                          textAlign: "center",
                          fontFamily: "Poppins",
                          fontWeight: "bold",
                          fontSize: "14px",
                          color: "white",
                          border: "1px solid #333",
                        }}
                      >
                        {flexRender(
                          header.column.columnDef.footer,
                          header.getContext()
                        )}
                      </TableCell>
                    ) : (
                      <TableCell key={header.id} sx={{ bgcolor: "#bf0202" }} />
                    )
                  )}
                </TableRow>
              ))}
            </TableFooter>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <CircularProgress color="inherit" />
      </Box>
    );
  }

  return <LocalTable data={data} columns={columns} />;
};

export default GamesList;
